import { Group } from 'three';
import { createMpRemoteChampionsController } from './mpRemoteChampions.js';
import { syncRemoteProjectileMeshes } from '../scene/remoteProjectileSceneManager.js';

const MOVE_SEND_MS = 33;

/**
 * @param {{
 *   scene: object,
 *   createChampionMesh: (key: string, team: string) => object,
 *   getPlayerEntity: () => object,
 *   THREE?: object
 * }} deps
 */
export function createMpPresenceSession(deps) {
  const { scene, createChampionMesh, getPlayerEntity } = deps;
  if (!scene || !createChampionMesh) return null;

  const THREE = deps.THREE || globalThis.THREE || null;
  const remotes = createMpRemoteChampionsController({
    scene,
    createGroup: () => new Group(),
    createMesh: createChampionMesh
  });
  /** @type {Map<string, object>} */
  const projectileMeshes = new Map();

  let client = null;
  let localClientId = null;
  let lastSend = 0;
  let placedLocal = false;

  function syncProjectiles(snapshot) {
    if (!THREE || !scene) return;
    syncRemoteProjectileMeshes(
      scene,
      snapshot?.projectiles || [],
      projectileMeshes,
      THREE
    );
  }

  function clearProjectiles() {
    syncRemoteProjectileMeshes(scene, [], projectileMeshes, THREE || {});
  }

  function attach(mpClient, match) {
    client = mpClient;
    localClientId = mpClient.getClientId();
    placedLocal = false;
    remotes.syncFromMatch(match, localClientId);
    const snap = mpClient.getSnapshot();
    if (snap) {
      remotes.applySnapshot(snap, localClientId);
      remotes.placeLocalFromSnapshot(snap, localClientId, getPlayerEntity());
      syncProjectiles(snap);
      placedLocal = true;
    }
    mpClient.onSnapshot((snapshot) => {
      remotes.applySnapshot(snapshot, localClientId);
      syncProjectiles(snapshot);
      if (!placedLocal) {
        remotes.placeLocalFromSnapshot(snapshot, localClientId, getPlayerEntity());
        placedLocal = true;
      }
    });
  }

  function tick(now = Date.now()) {
    remotes.updateFrame(now);
    if (!client || typeof client.sendMove !== 'function') return;
    const ent = getPlayerEntity();
    if (!ent) return;
    if (now - lastSend < MOVE_SEND_MS) return;
    lastSend = now;
    client.sendMove(ent.x, ent.z);
  }

  function detach() {
    remotes.clear();
    clearProjectiles();
    client = null;
    localClientId = null;
    placedLocal = false;
  }

  return {
    attach,
    tick,
    detach,
    remotes,
    getProjectileMeshes: () => projectileMeshes
  };
}
